<template>
  <v-main>
    <v-container fluid>
      <!-- Header Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Bank Details Approval
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Review and approve pending bank account details
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="warning" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-clock-alert</v-icon>
                      {{ pendingCount }} Pending
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="goBack"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Bank Details
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Filtration Section -->
            <v-card-text class="px-4 py-3">
              <div class="form-section">
                <h3 class="section-title mb-4">
                  <v-icon color="primary" class="mr-2"
                    >mdi-filter-variant</v-icon
                  >
                  Filter Bank Details
                </h3>
                <div class="d-flex align-center justify-between mb-3">
                  <div class="d-flex align-center">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                  </div>
                  <v-btn
                    v-if="hasActiveFilters"
                    text
                    x-small
                    color="error"
                    @click="clearAllFilters"
                  >
                    <v-icon left x-small>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </div>

                <v-row>
                  <!-- Search Field -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="filter.search_keyword"
                      label="Search Bank Details *"
                      placeholder="Account name, bank name, account number..."
                      outlined
                      clearable
                      class="professional-field"
                      hide-details="auto"
                      @input="handleSearch"
                    >
                      <template #prepend-inner>
                        <v-icon color="grey">mdi-magnify</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Sort Options -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="filter.sort_by"
                      :items="sortOptions"
                      label="Sort by"
                      placeholder="Select sort order"
                      outlined
                      class="professional-field"
                      hide-details="auto"
                      @change="applyFilters"
                    >
                      <template #prepend-inner>
                        <v-icon color="grey">mdi-sort</v-icon>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <v-card-title class="page-header py-3 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h3 class="section-title mb-0">
                        <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                        Bank Details List
                      </h3>
                    </div>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-bank</v-icon>
                      {{ totalItems }} {{ totalItems === 1 ? "Item" : "Items" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="totalItems > 0">
                      {{ (currentPage - 1) * rowsPerPage + 1 }}-{{
                        Math.min(currentPage * rowsPerPage, totalItems)
                      }}
                      of {{ totalItems }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedRowsPerPage"
                        dense
                        class="mr-2"
                        @change="changeRowsPerPage"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-data-table
              :headers="headers"
              :items="bankDetails"
              :loading="loading"
              :items-per-page="rowsPerPage"
              :page.sync="currentPage"
              :server-items-length="totalItems"
              hide-default-footer
              class="enhanced-table elevation-0"
              item-key="id"
            >
              <!-- Loading slot -->
              <template #loading>
                <div class="text-center pa-4">
                  <health-loader message="Loading bank details..." />
                </div>
              </template>

              <!-- No data slot -->
              <template #no-data>
                <div class="text-center pa-8">
                  <v-icon color="grey lighten-1" size="60"
                    >mdi-bank-outline</v-icon
                  >
                  <div class="text-h6 grey--text mt-4">
                    No pending bank details found
                  </div>
                  <div class="text-body-2 grey--text">
                    All bank details have been reviewed
                  </div>
                </div>
              </template>

              <!-- Serial Number Column -->
              <template #[`item.sr_no`]="{ index }">
                <span class="font-weight-medium primary--text">
                  {{ (currentPage - 1) * rowsPerPage + index + 1 }}
                </span>
              </template>

              <!-- User Info Column -->
              <template #[`item.user_info`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" class="mr-3">
                    <v-img
                      v-if="item.user && item.user.image_path"
                      :src="getImageUrl(item.user.image_path)"
                      :alt="item.user.first_name"
                    ></v-img>
                    <v-icon v-else color="grey lighten-1">mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium text-subtitle-2">
                      {{ item.user ? getFullName(item.user) : "N/A" }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.user">
                      {{ item.user.email || "No email" }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Bank Details Column -->
              <template #[`item.bank_info`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.bank_name }}</div>
                  <div class="text-caption grey--text">
                    {{ item.branch || "No branch specified" }}
                  </div>
                </div>
              </template>

              <!-- Account Details Column -->
              <template #[`item.account_info`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.account_name }}</div>
                  <div class="text-caption grey--text">
                    {{ item.account_number }}
                  </div>
                  <div class="text-caption grey--text" v-if="item.ifsc_code">
                    IFSC: {{ item.ifsc_code }}
                  </div>
                </div>
              </template>

              <!-- Created By Column -->
              <template #[`item.created_by`]="{ item }">
                <div v-if="item.created_by">
                  <div class="font-weight-medium">
                    {{
                      item.created_by.first_name +
                      " " +
                      item.created_by.last_name
                    }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ item.created_by.email }}
                  </div>
                </div>
                <span v-else class="grey--text">System</span>
              </template>

              <!-- Created At Column -->
              <template #[`item.created_at`]="{ item }">
                <div>
                  <div class="text-body-2">
                    {{ formatDate(item.created_at) }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ formatTime(item.created_at) }}
                  </div>
                </div>
              </template>

              <!-- Actions Column -->
              <template #[`item.actions`]="{ item }">
                <div class="d-flex">
                  <v-btn
                    color="success"
                    small
                    outlined
                    class="mr-1"
                    @click="openApproveDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    @click="openRejectDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-close</v-icon>
                    Reject
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Enhanced Pagination like users index -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (currentPage - 1) * rowsPerPage + 1 }}-{{
                        Math.min(currentPage * rowsPerPage, totalItems)
                      }}
                      of {{ totalItems }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="currentPage <= 1"
                      @click="
                        currentPage--;
                        handlePageChange();
                      "
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="currentPage"
                      :length="paginationLength"
                      :total-visible="4"
                      circle
                      class="compact-pagination mx-1"
                      @input="handlePageChange"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="currentPage >= paginationLength"
                      @click="
                        currentPage++;
                        handlePageChange();
                      "
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Approve Dialog -->
    <v-dialog v-model="approveDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Approve Bank Details
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Are you sure you want to approve these bank details?</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-bank</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ approveDialog.bankDetail?.account_name }}
                </div>
                <div class="text-caption grey--text">
                  {{ approveDialog.bankDetail?.bank_name }} -
                  {{ approveDialog.bankDetail?.account_number }}
                </div>
              </div>
            </div>
          </div>

          <v-textarea
            v-model="approveDialog.remark"
            label="Approval Remark (Optional)"
            placeholder="Add any notes about this approval..."
            outlined
            rows="3"
            counter="500"
            :rules="[
              (v) =>
                !v ||
                v.length <= 500 ||
                'Remark must be 500 characters or less',
            ]"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeApproveDialog">Cancel</v-btn>
          <v-btn
            color="success"
            @click="approveBankDetails"
            :loading="approveDialog.loading"
          >
            <v-icon left>mdi-check</v-icon>
            Approve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          Reject Bank Details
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Please provide a reason for rejecting these bank details:</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-bank</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ rejectDialog.bankDetail?.account_name }}
                </div>
                <div class="text-caption grey--text">
                  {{ rejectDialog.bankDetail?.bank_name }} -
                  {{ rejectDialog.bankDetail?.account_number }}
                </div>
              </div>
            </div>
          </div>

          <v-textarea
            v-model="rejectDialog.remark"
            label="Rejection Reason *"
            placeholder="Please explain why these bank details are being rejected..."
            outlined
            rows="4"
            counter="1000"
            :rules="rejectRemarkRules"
            :error-messages="rejectDialog.errors.rejection_reason"
            required
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeRejectDialog">Cancel</v-btn>
          <v-btn
            color="error"
            @click="rejectBankDetails"
            :loading="rejectDialog.loading"
            :disabled="!rejectDialog.remark || rejectDialog.remark.length < 5"
          >
            <v-icon left>mdi-close</v-icon>
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar.show"
      :color="successSnackbar.color"
      :timeout="4000"
      top
    >
      {{ successSnackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="successSnackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from "../../axios";

export default {
  name: "BankDetailsApproval",
  data() {
    return {
      loading: false,
      bankDetails: [],
      totalItems: 0,
      currentPage: 1,
      rowsPerPage: 25,
      selectedRowsPerPage: 25,
      loadingActions: {},

      // Filters
      filter: {
        search_keyword: "",
        sort_by: "created_at",
      },

      // Header configuration
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        { text: "User", value: "user_info", sortable: false, width: "200px" },
        {
          text: "Bank Details",
          value: "bank_info",
          sortable: false,
          width: "180px",
        },
        {
          text: "Account Details",
          value: "account_info",
          sortable: false,
          width: "220px",
        },
        {
          text: "Submitted By",
          value: "created_by",
          sortable: false,
          width: "150px",
        },
        { text: "Submitted On", value: "created_at", width: "150px" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: "200px",
        },
      ],

      // Sort options
      sortOptions: [
        { text: "Oldest First", value: "created_at" },
        { text: "Newest First", value: "created_at_desc" },
        { text: "Account Name A-Z", value: "account_name" },
        { text: "Account Name Z-A", value: "account_name_desc" },
        { text: "Bank Name A-Z", value: "bank_name" },
        { text: "Bank Name Z-A", value: "bank_name_desc" },
      ],

      // Dialogs
      approveDialog: {
        show: false,
        bankDetail: null,
        remark: "",
        loading: false,
      },
      rejectDialog: {
        show: false,
        bankDetail: null,
        remark: "",
        loading: false,
        errors: {},
      },

      // Snackbar
      successSnackbar: {
        show: false,
        message: "",
        color: "success",
      },

      // Search debounce
      searchTimeout: null,

      // Jump to page functionality
      jumpToPage: null,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.rowsPerPage);
    },

    pendingCount() {
      return this.totalItems;
    },

    hasActiveFilters() {
      return !!this.filter.search_keyword;
    },

    rejectRemarkRules() {
      return [
        (v) => !!v || "Rejection reason is required",
        (v) =>
          (v && v.length >= 5) ||
          "Rejection reason must be at least 5 characters",
        (v) =>
          (v && v.length <= 1000) ||
          "Rejection reason must be 1000 characters or less",
      ];
    },

    paginationLength() {
      return Math.ceil(this.totalItems / this.rowsPerPage);
    },
  },

  // watch: {
  //   currentPage() {
  //     this.fetchBankDetails();
  //   },
  // },

  async mounted() {
    await this.fetchBankDetails();
  },

  methods: {
    async fetchBankDetails() {
      console.log("Fetching Bank Details for Approval");
      try {
        this.loading = true;

        // Clean filter parameters like users index
        const cleanedFilter = { ...this.filter };
        Object.keys(cleanedFilter).forEach((key) => {
          if (cleanedFilter[key] === null || cleanedFilter[key] === "") {
            delete cleanedFilter[key];
          }
        });

        const params = {
          page: this.currentPage,
          rowsPerPage: this.rowsPerPage,
          status: 0, // 0 = Pending approval
          ...cleanedFilter,
        };

        // Handle sort parameters
        if (this.filter.sort_by.includes("_desc")) {
          params.sort_by = this.filter.sort_by.replace("_desc", "");
          params.sort_order = "desc";
        } else {
          params.sort_by = this.filter.sort_by;
          params.sort_order = "asc";
        }

        const response = await axios.get("/bank_details/approvals", { params });

        if (response.data.success) {
          this.bankDetails = response.data.data;
          this.totalItems = response.data.count;
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to fetch bank details",
          });
        }
      } catch (error) {
        console.error("Error fetching pending bank details:", error);
        if (error.response && error.response.status === 401) {
          // Handle authentication errors like users index
          window.location.href = "/auth/login";
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading pending bank details data",
          });
        }
      } finally {
        this.loading = false;
      }
    },

    // Filter methods
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchBankDetails();
      }, 500);
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchBankDetails();
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: "",
        sort_by: "created_at",
      };
      this.applyFilters();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      return count;
    },

    // Pagination methods
    handlePageChange() {
      this.fetchBankDetails();
    },

    handlePageSizeChange() {
      this.currentPage = 1;
      this.fetchBankDetails();
    },

    changeRowsPerPage() {
      this.rowsPerPage = this.selectedRowsPerPage;
      this.currentPage = 1;
      this.fetchBankDetails();
    },

    // Jump to page functionality like users index
    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.currentPage = targetPage;
        this.fetchBankDetails();
      }
      this.jumpToPage = null;
    },

    // Dialog methods
    openApproveDialog(bankDetail) {
      this.approveDialog = {
        show: true,
        bankDetail: bankDetail,
        remark: "",
        loading: false,
      };
    },

    closeApproveDialog() {
      this.approveDialog = {
        show: false,
        bankDetail: null,
        remark: "",
        loading: false,
      };
    },

    openRejectDialog(bankDetail) {
      this.rejectDialog = {
        show: true,
        bankDetail: bankDetail,
        remark: "",
        loading: false,
        errors: {},
      };
    },

    closeRejectDialog() {
      this.rejectDialog = {
        show: false,
        bankDetail: null,
        remark: "",
        loading: false,
        errors: {},
      };
    },

    // Approval methods
    async approveBankDetails() {
      try {
        this.approveDialog.loading = true;
        this.loadingActions[this.approveDialog.bankDetail.id] = true;

        const data = {
          approval_remark: this.approveDialog.remark || null,
        };

        const response = await axios.post(
          `/bank_details/${this.approveDialog.bankDetail.id}/approve`,
          data
        );

        if (response.data.success) {
          this.showSnackbar("Bank details approved successfully", "success");
          this.closeApproveDialog();
          await this.fetchBankDetails();
        }
      } catch (error) {
        console.error("Error approving bank details:", error);
        this.showSnackbar("Error approving bank details", "error");
      } finally {
        this.approveDialog.loading = false;
        delete this.loadingActions[this.approveDialog.bankDetail?.id];
      }
    },

    async rejectBankDetails() {
      try {
        this.rejectDialog.loading = true;
        this.rejectDialog.errors = {};
        this.loadingActions[this.rejectDialog.bankDetail.id] = true;

        const data = {
          rejection_reason: this.rejectDialog.remark,
        };

        const response = await axios.post(
          `/bank_details/${this.rejectDialog.bankDetail.id}/reject`,
          data
        );

        if (response.data.success) {
          this.showSnackbar("Bank details rejected successfully", "success");
          this.closeRejectDialog();
          await this.fetchBankDetails();
        }
      } catch (error) {
        console.error("Error rejecting bank details:", error);
        if (error.response?.data?.errors) {
          this.rejectDialog.errors = error.response.data.errors;
        } else {
          this.showSnackbar("Error rejecting bank details", "error");
        }
      } finally {
        this.rejectDialog.loading = false;
        delete this.loadingActions[this.rejectDialog.bankDetail?.id];
      }
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    showSnackbar(message, color = "success") {
      this.successSnackbar = {
        show: true,
        message: message,
        color: color,
      };
    },

    goBack() {
      this.$router.back();
    },

    // Enhanced image URL generation like users index
    getImageUrl(imagePath) {
      if (!imagePath) return "";
      return `${
        this.mediaUrl || process.env.VUE_APP_MEDIA_URL || "/storage"
      }/${imagePath}`;
    },

    // Enhanced full name formatting like users index
    getFullName(user) {
      if (!user) return "Unknown User";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || user.email || "No name";
    },

    // Enhanced currency formatting like users index
    formatCurrency(amount) {
      const value = parseFloat(amount) || 0;
      return value.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    // Enhanced encrypted ID functionality like users index
    encryptIt(id) {
      // This should match the encryption method used in users index
      return id; // Placeholder - should use actual encryption if available
    },
  },
};
</script>

<style scoped>
/* Form Card Styling - Matching Create Page */
.form-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.page-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.professional-field >>> .v-input__control {
  border-radius: 8px !important;
}

.professional-field >>> .v-text-field__details {
  margin-top: 8px;
}

/* Enhanced table styling like users index */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact pagination styling like users index */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}
</style>